import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule, NgIf, NgFor } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClientsService } from '../../core/services/clients.service';
import { MealRecordsService } from '../../core/services/meal-records.service';
import { Client } from '../../core/models/client.model';
import { MealRecord, CreateMealRecordDto, UpdateMealRecordDto } from '../../core/models/meal-record.model';

@Component({
  selector: 'app-daily-entry',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  styles: [`
    .page-header { display: flex; flex-direction: column; gap: 4px; margin-bottom: 16px; }
    .page-title { margin: 0; font-weight: 600; letter-spacing: 0.2px; color: #1F2937; }
    .page-subtitle { margin: 0; color: rgba(0,0,0,0.6); font-size: 13px; }
    .header-actions { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
    .card { margin-bottom: 16px; border: 1px solid rgba(0,0,0,0.08); border-radius: 8px; overflow: hidden; }
    .card-body { padding: 16px; }
    .filters-row { display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-end; }
    .filters-row .mat-mdc-form-field { min-width: 220px; }
    table { width: 100%; }
  `],
  template: `
    <div class="page-header">
      <h2 class="page-title">{{ 'dailyEntry.title' | translate }}</h2>
      <p class="page-subtitle">{{ 'dailyEntry.subtitle' | translate }}</p>
    </div>

    <div class="header-actions">
      <span></span>
      <button mat-raised-button color="primary" class="create-button" (click)="openCreate()">
        <mat-icon>add</mat-icon>
        {{ 'dailyEntry.addRecord' | translate }}
      </button>
    </div>

    <mat-card class="card">
      <div class="card-body">
        <div style="margin-bottom: 8px; font-weight: 600; display: flex; align-items: center; gap: 8px;">
          <mat-icon>filter_list</mat-icon>
          {{ 'dailyEntry.filters' | translate }}
        </div>
        <div class="filters-row">
          <mat-form-field appearance="outline">
            <mat-label>{{ 'common.date' | translate }}</mat-label>
            <input matInput [matDatepicker]="filterDatePicker" [value]="filterDate" (dateChange)="onDateFilterChange($event.value)">
            <mat-datepicker-toggle matSuffix [for]="filterDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #filterDatePicker></mat-datepicker>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'common.client' | translate }}</mat-label>
            <mat-select [(value)]="filterClientId" (selectionChange)="onClientFilterChange()">
              <mat-option value="">{{ 'common.allClients' | translate }}</mat-option>
              <mat-option *ngFor="let c of clients" [value]="c.id">{{ c.name }}</mat-option>
            </mat-select>
          </mat-form-field>
          <button mat-stroked-button (click)="clearFilters()" *ngIf="hasActiveFilters()" style="align-self: flex-end; margin-bottom: 1.34375em;">
            {{ 'dailyEntry.clearFilters' | translate }}
          </button>
        </div>
      </div>
    </mat-card>

    <mat-card class="card">
      <div class="card-body" *ngIf="loading" style="display: flex; justify-content: center; padding: 8px 0;">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
      <div class="card-body" *ngIf="!loading && !records.length">{{ 'dailyEntry.noRecords' | translate }}</div>

      <table mat-table [dataSource]="records" *ngIf="!loading && records.length">
        <ng-container matColumnDef="date">
          <th mat-header-cell *matHeaderCellDef>{{ 'common.date' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.date | date:'mediumDate' }}</td>
        </ng-container>
        <ng-container matColumnDef="client">
          <th mat-header-cell *matHeaderCellDef>{{ 'common.client' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.clientName }}</td>
        </ng-container>
        <ng-container matColumnDef="breakfast">
          <th mat-header-cell *matHeaderCellDef>{{ 'daily.breakfast' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.breakfastQty }}</td>
        </ng-container>
        <ng-container matColumnDef="lunch">
          <th mat-header-cell *matHeaderCellDef>{{ 'daily.lunch' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.lunchQty }}</td>
        </ng-container>
        <ng-container matColumnDef="dinner">
          <th mat-header-cell *matHeaderCellDef>{{ 'daily.dinner' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.dinnerQty }}</td>
        </ng-container>
        <ng-container matColumnDef="lunchPackage">
          <th mat-header-cell *matHeaderCellDef>{{ 'daily.lunchPackage' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.lunchPackageQty }}</td>
        </ng-container>
        <ng-container matColumnDef="total">
          <th mat-header-cell *matHeaderCellDef>{{ 'daily.total' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.breakfastQty + r.lunchQty + r.dinnerQty + r.lunchPackageQty }}</td>
        </ng-container>
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="table-actions">{{ 'common.actions' | translate }}</th>
          <td mat-cell *matCellDef="let r" class="table-actions">
            <button mat-icon-button class="action-button edit-button" (click)="openEdit(r)"><mat-icon svgIcon="edit-custom"></mat-icon></button>
            <button mat-icon-button class="action-button delete-button" (click)="confirmDelete(r)"><mat-icon svgIcon="delete-custom"></mat-icon></button>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
      </table>
    </mat-card>
  `
})
export class DailyEntryComponent implements OnInit {
  clients: Client[] = [];
  records: MealRecord[] = [];
  loading = false;
  filterDate: Date | null = null;
  filterClientId = '';
  displayedColumns = ['date', 'client', 'breakfast', 'lunch', 'dinner', 'lunchPackage', 'total', 'actions'];

  constructor(
    private clientsService: ClientsService,
    private recordsService: MealRecordsService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData() {
    this.loading = true;
    this.clientsService.getAll().subscribe({
      next: list => {
        this.clients = list.filter(c => c.active);
        this.loadRecords();
      },
      error: () => { this.loading = false; }
    });
  }

  loadRecords() {
    this.loading = true;
    const filter: any = {};
    if (this.filterClientId) filter.clientId = this.filterClientId;
    if (this.filterDate) {
      const d = new Date(this.filterDate);
      // Create UTC date at noon to avoid timezone issues
      const dateStr = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0)).toISOString();
      filter.from = dateStr;
      filter.to = dateStr;
    }
    console.log('Daily Entry - Loading records with filter:', filter, 'filterDate:', this.filterDate, 'filterClientId:', this.filterClientId);
    this.recordsService.getAll(filter).subscribe({
      next: list => {
        console.log('Daily Entry - Received records:', list.length, list);
        this.records = list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        this.loading = false;
      },
      error: (err) => {
        console.error('Daily Entry - Error loading records:', err);
        this.loading = false;
      }
    });
  }

  onDateFilterChange(date: Date | null) {
    console.log('Daily Entry - Date filter changed to:', date);
    this.filterDate = date;
    this.loadRecords();
  }

  onClientFilterChange() {
    console.log('Daily Entry - Client filter changed to:', this.filterClientId);
    this.loadRecords();
  }

  hasActiveFilters(): boolean {
    return this.filterDate !== null || this.filterClientId !== '';
  }

  clearFilters() {
    this.filterDate = null;
    this.filterClientId = '';
    this.loadRecords();
  }

  openCreate() {
    const ref = this.dialog.open(DailyEntryDialogComponent, { data: { clients: this.clients, record: null }, width: '600px' });
    ref.afterClosed().subscribe((dto?: CreateMealRecordDto) => {
      if (!dto) return;
      this.recordsService.create(dto).subscribe({
        next: () => {
          this.snack.open(this.t.instant('dailyEntry.created'), this.t.instant('common.close'), { duration: 2000 });
          this.loadRecords();
        },
        error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.saveError'), this.t.instant('common.close'), { duration: 3000 })
      });
    });
  }

  openEdit(record: MealRecord) {
    const ref = this.dialog.open(DailyEntryDialogComponent, { data: { clients: this.clients, record }, width: '600px' });
    ref.afterClosed().subscribe((dto?: UpdateMealRecordDto) => {
      if (!dto) return;
      this.recordsService.update(record.id, dto).subscribe({
        next: () => {
          this.snack.open(this.t.instant('dailyEntry.updated'), this.t.instant('common.close'), { duration: 2000 });
          this.loadRecords();
        },
        error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.saveError'), this.t.instant('common.close'), { duration: 3000 })
      });
    });
  }

  confirmDelete(record: MealRecord) {
    const yes = confirm(this.t.instant('dailyEntry.confirmDelete'));
    if (!yes) return;
    this.recordsService.delete(record.id).subscribe({
      next: () => {
        this.snack.open(this.t.instant('dailyEntry.deleted'), this.t.instant('common.close'), { duration: 2000 });
        this.loadRecords();
      },
      error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.deleteError'), this.t.instant('common.close'), { duration: 3000 })
    });
  }
}

@Component({
  selector: 'app-daily-entry-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    TranslateModule,
    NgIf,
    NgFor
  ],
  template: `
    <h2 mat-dialog-title>{{ data.record ? ('dailyEntry.editRecord' | translate) : ('dailyEntry.addRecord' | translate) }}</h2>
    <form [formGroup]="form" (ngSubmit)="save()" style="padding: 0 24px 16px 24px; display: block;">
      <mat-form-field style="width: 100%;" appearance="outline">
        <mat-label>{{ 'common.date' | translate }} *</mat-label>
        <input matInput [matDatepicker]="picker" formControlName="date" required>
        <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-datepicker #picker></mat-datepicker>
      </mat-form-field>
      <mat-form-field style="width: 100%;" appearance="outline">
        <mat-label>{{ 'common.client' | translate }} *</mat-label>
        <mat-select formControlName="clientId" required>
          <mat-option *ngFor="let c of sortedClients" [value]="c.id">{{ c.name }}</mat-option>
        </mat-select>
      </mat-form-field>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
        <mat-form-field appearance="outline">
          <mat-label>{{ 'daily.breakfast' | translate }}</mat-label>
          <input matInput type="number" min="0" formControlName="breakfastQty">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'daily.lunch' | translate }}</mat-label>
          <input matInput type="number" min="0" formControlName="lunchQty">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'daily.dinner' | translate }}</mat-label>
          <input matInput type="number" min="0" formControlName="dinnerQty">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'daily.lunchPackage' | translate }}</mat-label>
          <input matInput type="number" min="0" formControlName="lunchPackageQty">
        </mat-form-field>
      </div>
      <mat-form-field style="width: 100%;" appearance="outline">
        <mat-label>{{ 'common.notes' | translate }}</mat-label>
        <textarea matInput rows="3" formControlName="notes"></textarea>
      </mat-form-field>
      <div style="margin-top: 12px; display: flex; gap: 8px; justify-content: flex-end;">
        <button mat-stroked-button mat-dialog-close type="button">{{ 'common.cancel' | translate }}</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="form.invalid">
          {{ data.record ? ('common.update' | translate) : ('common.create' | translate) }}
        </button>
      </div>
    </form>
  `
})
export class DailyEntryDialogComponent implements OnInit {
  sortedClients: Client[] = [];
  form = this.fb.group({
    clientId: ['', [Validators.required]],
    date: [new Date(), [Validators.required]],
    breakfastQty: [0, [Validators.min(0)]],
    lunchQty: [0, [Validators.min(0)]],
    dinnerQty: [0, [Validators.min(0)]],
    lunchPackageQty: [0, [Validators.min(0)]],
    notes: ['']
  });

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DailyEntryDialogComponent>,
    private recordsService: MealRecordsService,
    @Inject(MAT_DIALOG_DATA) public data: { clients: Client[], record: MealRecord | null }
  ) {}

  ngOnInit(): void {
    // Sort clients by meal count (descending) then by name
    this.loadClientStats();

    if (this.data.record) {
      this.form.patchValue({
        clientId: this.data.record.clientId,
        date: new Date(this.data.record.date),
        breakfastQty: this.data.record.breakfastQty,
        lunchQty: this.data.record.lunchQty,
        dinnerQty: this.data.record.dinnerQty,
        lunchPackageQty: this.data.record.lunchPackageQty,
        notes: this.data.record.notes || ''
      });
    }
  }

  loadClientStats() {
    // Load all records to calculate meal counts per client
    const clientCounts = new Map<string, number>();
    this.recordsService.getAll().subscribe({
      next: records => {
        records.forEach(r => {
          const count = (r.breakfastQty || 0) + (r.lunchQty || 0) + (r.dinnerQty || 0) + (r.lunchPackageQty || 0);
          clientCounts.set(r.clientId, (clientCounts.get(r.clientId) || 0) + count);
        });
        this.sortedClients = [...this.data.clients].sort((a, b) => {
          const countA = clientCounts.get(a.id) || 0;
          const countB = clientCounts.get(b.id) || 0;
          if (countB !== countA) return countB - countA;
          return a.name.localeCompare(b.name);
        });
      },
      error: () => {
        this.sortedClients = [...this.data.clients].sort((a, b) => a.name.localeCompare(b.name));
      }
    });
  }

  save() {
    if (this.form.invalid) return;
    const v = this.form.value;
    const date = v.date as Date;
    // Create UTC date at noon to avoid timezone issues
    const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0));
    const dto: CreateMealRecordDto = {
      clientId: v.clientId!,
      date: utcDate.toISOString(),
      breakfastQty: Number(v.breakfastQty || 0),
      lunchQty: Number(v.lunchQty || 0),
      dinnerQty: Number(v.dinnerQty || 0),
      lunchPackageQty: Number(v.lunchPackageQty || 0),
      notes: v.notes || null
    };
    this.dialogRef.close(dto);
  }
}
